import { NormalizedAlert, AlertResponseMetadata } from '../types/alert';
import { APP_CONFIG } from '../config/appConfig';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  etag?: string | null;
}

const CACHE_PREFIX = 'sn_cache_';

export class AlertCache {
  private static memoryCache = new Map<string, CacheEntry<any>>();

  static set<T>(key: string, data: T, etag?: string | null): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      etag,
    };

    // Store in memory
    this.memoryCache.set(key, entry);

    // Also persist in sessionStorage or localStorage if available
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(
          CACHE_PREFIX + key,
          JSON.stringify(entry)
        );
      }
    } catch (e) {
      // Storage quota exceeded or disabled in private browsing - memory cache still works
      console.warn('LocalStorage caching unavailable, using memory cache:', e);
    }
  }

  static get<T>(key: string): { data: T; ageSeconds: number; isStale: boolean } | null {
    let entry: CacheEntry<T> | undefined = this.memoryCache.get(key);

    if (!entry && typeof window !== 'undefined' && window.localStorage) {
      try {
        const stored = localStorage.getItem(CACHE_PREFIX + key);
        if (stored) {
          entry = JSON.parse(stored);
          if (entry) {
            this.memoryCache.set(key, entry);
          }
        }
      } catch (e) {
        entry = undefined;
      }
    }

    if (!entry) return null;

    const ageSeconds = Math.floor((Date.now() - entry.timestamp) / 1000);
    const isStale = ageSeconds > APP_CONFIG.nws.cacheTtlSeconds;

    return {
      data: entry.data,
      ageSeconds,
      isStale,
    };
  }

  static getLastSuccessfulTime(key: string): string | null {
    const item = this.get(key);
    if (!item) return null;
    return new Date(Date.now() - item.ageSeconds * 1000).toISOString();
  }

  static clear(): void {
    this.memoryCache.clear();
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        Object.keys(localStorage)
          .filter(k => k.startsWith(CACHE_PREFIX))
          .forEach(k => localStorage.removeItem(k));
      }
    } catch {
      // ignore
    }
  }
}

